// @flow
/* -----------------Globals--------------- */
import React, { useState } from 'react';
import dynamic from 'next/dynamic';

/* -----------------UI--------------- */
import {
  Box,
  Grid,
  Card,
  Container,
  withStyles,
  ListSubheader,
  Button,
  CardActionArea,
  LinearProgress,
} from '@material-ui/core';
import { RadioboxBlank, RadioboxMarked } from 'mdi-material-ui';
import clsx from 'clsx';

/* -----------------Child components--------------- */
import Breadcrumbs from '@components/common/Breadcrumbs';

const RenderMarkdown = dynamic(() => import('@components/common/RenderMarkdown'), { ssr: false });

/* -----------------Type declarations--------------- */
type Classes = {
  card: string,
  selected: string,
  choice: string,
  iconSelected: string,
  bar: string,
  barRoot: string,
}
/* -----------------Props--------------- */
type Props = {
  classes: Classes,
  stem: any,
  choices: any,
  breadcrumbs: any,
  questionSetNumericId: number
}
/* -----------------Styles--------------- */
const styles = theme => ({
  card: {
    width: '100%',
  },
  selected: {},
  choice: {
    borderStyle: 'solid',
    borderWidth: 2,
    transition: '0.2s all ease-out',
    borderColor: theme.palette.background.paper,
    '&$selected': {
      transition: '0.2s all ease-out',
      backgroundColor: theme.palette.selected.background,
      borderColor: theme.palette.selected.border,
    },
  },
  iconSelected: {
    color: theme.palette.selected.border,
  },
  barRoot: {
    height: 8,
    flex: 1,
    backgroundColor: theme.palette.tapColor,
    width: '100%',
    borderRadius: 20,
  },
  bar: {
    borderRadius: 20,
    backgroundColor: theme.palette.secondary.main,
  },

});
/* -----------------Component--------------- */

const QuestionUnauth = (props: Props) => {
  const {
    classes,
    stem,
    choices: answers,
    breadcrumbs,
    questionSetNumericId,
  } = props;

  const breadcrumbValues = breadcrumbs.map(({ name, item }) => ({
    title: name,
    link: item,
  }));

  breadcrumbValues.pop();

  const [selectedAnswer, setSelectedAnswer] = useState();

  const toggleSelected = (id) => {
    setSelectedAnswer(id === selectedAnswer ? false : id);
  };

  const { explanation } = answers.find(({ isCorrect }) => isCorrect);
  const Choice = ({ text, id }) => {
    const isSelected = selectedAnswer === id;
    return (
      <Card
        elevation={0}
        onClick={() => toggleSelected(id)}
        key={`choice-${id}`}
        classes={{ root: clsx(classes.choice, isSelected && classes.selected) }}
      >
        <CardActionArea>
          <Box p={2} display="flex" flexDirection="row" alignItems="center">
            <Box mr={2} display="flex" flexDirection="row" alignItems="center">
              {isSelected ? <RadioboxMarked className={classes.iconSelected} /> : <RadioboxBlank />}
            </Box>
            <RenderMarkdown source={text} variant="answer" />
          </Box>
        </CardActionArea>
      </Card>
    );
  };

  return (
    <Box pt={3} pb={6} style={{ userSelect: 'none' }}>
      <Container maxWidth="md">
        <Box mb={3}>
          <Breadcrumbs breadcrumbValues={breadcrumbValues} />
        </Box>
        <Box mb={3}>
          <LinearProgress
            color="secondary"
            variant="determinate"
            value={25}
            classes={{ bar: classes.bar, root: classes.barRoot }}
          />
        </Box>
        <Grid container direction="column" spacing={1}>
          <Grid item xs={12}>
            <Card elevation={0}>
              <Box p={3}>
                <RenderMarkdown source={stem} variant="stem" />
              </Box>
            </Card>
          </Grid>
          <ListSubheader>Answers</ListSubheader>
          {answers.map(({ id, text }) => (
            <Grid item xs={12} key={id}>
              <Choice text={text} id={id} />
            </Grid>
          ))}
          <ListSubheader>Explanation</ListSubheader>
          <Card>
            <Box p={3} position="relative">
              <Box
                position="absolute"
                top={0}
                left={0}
                width="100%"
                height="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                zIndex={100}
              >
                <Button
                  href={`/questions/${questionSetNumericId}`}
                  variant="contained"
                  color="primary"
                >
                  SEE ANSWER AND EXPLANATION
                </Button>
              </Box>
              <Box
                style={{ filter: 'blur(5px)' }}
              >
                <RenderMarkdown source={explanation} variant="explanation" />
              </Box>
            </Box>
          </Card>
        </Grid>
      </Container>
    </Box>
  );
};

export default withStyles(styles)(QuestionUnauth);
